import React, { useState } from "react";
import { ShieldAlert, Database, Play, Pause, RotateCcw } from "lucide-react";

export default function AdminOps({ shipments, onUpdateShipment, onResetData }) {
  const [selectedAwb, setSelectedAwb] = useState(shipments[0]?.awb || "");
  const [simulationActive, setSimulationActive] = useState(false);
  const [simulationTimer, setSimulationTimer] = useState(null);

  const selectedShipment =
    shipments.find((s) => s.awb === selectedAwb) || shipments[0];

  const handleStatusChange = (newStatus) => {
    if (!selectedShipment) return;

    // Create a copy of milestones to update completion states
    const statusSequence = [
      "booked",
      "dispatched",
      "in_transit",
      "reached_hub",
      "out_for_delivery",
      "delivered",
    ];

    const currentIdx = statusSequence.indexOf(newStatus);

    const updatedMilestones = selectedShipment.milestones.map((m, index) => {
      const seqIdx = statusSequence.indexOf(m.status);
      const isCompleted = seqIdx <= currentIdx;

      return {
        ...m,
        completed: isCompleted,
        timestamp:
          isCompleted && m.timestamp === "Pending"
            ? new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            : m.timestamp,
      };
    });

    // Update coordinates completion based on status
    const updatedRouteCoords = selectedShipment.routeCoordinates.map(
      (pt, index) => {
        let isCompleted = false;
        if (newStatus === "booked" && index === 0) isCompleted = true;
        if (newStatus === "dispatched" && index <= 1) isCompleted = true;
        if (newStatus === "in_transit" && index <= 1) isCompleted = true;
        if (newStatus === "reached_hub" && index <= 2) isCompleted = true;
        if (newStatus === "out_for_delivery" && index <= 2) isCompleted = true;
        if (newStatus === "delivered") isCompleted = true;

        return { ...pt, isCompleted };
      },
    );

    // Determine representative location name
    let locName = selectedShipment.currentLocation.name;
    if (newStatus === "booked")
      locName = `${selectedShipment.originCity} Sortation Center`;
    if (newStatus === "dispatched")
      locName = `${selectedShipment.originCity} Transit Runway`;
    if (newStatus === "reached_hub")
      locName = `${selectedShipment.destinationCity} Regional Sorter Hub`;
    if (newStatus === "out_for_delivery")
      locName = `${selectedShipment.destinationCity} Last Mile Facility`;
    if (newStatus === "delivered")
      locName = `${selectedShipment.destinationCity} Doorstep`;

    // Map latitude midway coordinates for smooth map plotting
    let lat = selectedShipment.currentLocation.lat;
    let lng = selectedShipment.currentLocation.lng;

    if (newStatus === "booked") {
      lat = selectedShipment.routeCoordinates[0]?.lat || 12.9716;
      lng = selectedShipment.routeCoordinates[0]?.lng || 77.5946;
    } else if (newStatus === "delivered") {
      const lastPt =
        selectedShipment.routeCoordinates[
          selectedShipment.routeCoordinates.length - 1
        ];
      lat = lastPt?.lat || 19.076;
      lng = lastPt?.lng || 72.8777;
    }

    const updated = {
      ...selectedShipment,
      status: newStatus,
      milestones: updatedMilestones,
      routeCoordinates: updatedRouteCoords,
      currentLocation: { lat, lng, name: locName },
    };

    onUpdateShipment(updated);
  };

  // Automated linehaul simulation loop (triggers status increments every 4 seconds)
  const toggleSimulation = () => {
    if (simulationActive) {
      if (simulationTimer) {
        clearInterval(simulationTimer);
        setSimulationTimer(null);
      }
      setSimulationActive(false);
    } else {
      setSimulationActive(true);
      const timer = setInterval(() => {
        // Trigger status increments
        const sequence = [
          "booked",
          "dispatched",
          "in_transit",
          "reached_hub",
          "out_for_delivery",
          "delivered",
        ];

        setSelectedAwb((currentAwb) => {
          const currentShip = shipments.find((s) => s.awb === currentAwb);
          if (currentShip) {
            const currentIdx = sequence.indexOf(currentShip.status);
            const nextIdx = (currentIdx + 1) % sequence.length;
            const nextStatus = sequence[nextIdx];
            // Invoke the state updater block
            const currentIdxSeq = sequence.indexOf(nextStatus);
            const updatedMilestones = currentShip.milestones.map((m) => {
              const seqIdx = sequence.indexOf(m.status);
              const isCompleted = seqIdx <= currentIdxSeq;
              return {
                ...m,
                completed: isCompleted,
                timestamp:
                  isCompleted && m.timestamp === "Pending"
                    ? new Date().toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : m.timestamp,
              };
            });

            const updatedRouteCoords = currentShip.routeCoordinates.map(
              (pt, index) => {
                let isCompleted = false;
                if (nextStatus === "booked" && index === 0) isCompleted = true;
                if (nextStatus === "dispatched" && index <= 1)
                  isCompleted = true;
                if (nextStatus === "in_transit" && index <= 1)
                  isCompleted = true;
                if (nextStatus === "reached_hub" && index <= 2)
                  isCompleted = true;
                if (nextStatus === "out_for_delivery" && index <= 2)
                  isCompleted = true;
                if (nextStatus === "delivered") isCompleted = true;
                return { ...pt, isCompleted };
              },
            );

            const updated = {
              ...currentShip,
              status: nextStatus,
              milestones: updatedMilestones,
              routeCoordinates: updatedRouteCoords,
            };
            onUpdateShipment(updated);
          }
          return currentAwb;
        });
      }, 4000);

      setSimulationTimer(timer);
    }
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-screen py-10">
      <div className="max-w-4xl mx-auto px-4 space-y-8">
        {/* Title Header */}
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 bg-rose-50 border border-rose-200 px-3 py-1 rounded-full text-xs text-rose-700 font-bold">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
            <span>Operations & Linehaul Dispatch Desk</span>
          </div>
          <h1 className="text-3xl font-sans font-black tracking-tight text-slate-900">
            JPSY Simulation Console
          </h1>
          <p className="text-slate-500 text-sm">
            Simulate Supply Chain triggers. Change package states instantly to
            watch live updates, milestone checklist events, and vehicle icons
            move on JPSY Maps in real time.
          </p>
        </div>

        {selectedShipment ? (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Left Selection list (5 Columns) */}
            <div className="md:col-span-5 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
              <h3 className="font-sans font-bold text-xs uppercase tracking-wider text-slate-400">
                Select Active Container Cargo
              </h3>

              <div className="space-y-2">
                {shipments.map((s) => (
                  <button
                    key={s.awb}
                    onClick={() => setSelectedAwb(s.awb)}
                    className={`w-full text-left p-3.5 rounded-xl border transition-all flex justify-between items-center cursor-pointer ${
                      selectedAwb === s.awb
                        ? "bg-rose-50/50 border-rose-300 ring-1 ring-rose-300"
                        : "bg-slate-50/40 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                    }`}
                  >
                    <div>
                      <p className="font-mono font-bold text-xs text-slate-900">
                        {s.awb}
                      </p>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {s.originCity} → {s.destinationCity}
                      </p>
                    </div>
                    <span className="text-[9px] uppercase font-mono font-bold px-2 py-0.5 rounded bg-slate-200 text-slate-800">
                      {s.status.replace("_", " ")}
                    </span>
                  </button>
                ))}
              </div>

              <button
                onClick={onResetData}
                className="w-full mt-4 border border-dashed border-slate-300 hover:border-slate-400 text-slate-500 hover:text-slate-800 text-xs font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Reset Sandbox Data</span>
              </button>
            </div>

            {/* Right Status Trigger Box (7 Columns) */}
            <div className="md:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-6">
              <div className="flex justify-between items-start border-b border-slate-100 pb-4">
                <div>
                  <h3 className="font-sans font-bold text-lg text-slate-950 font-mono">
                    AWB: {selectedShipment.awb}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {selectedShipment.senderName} to{" "}
                    {selectedShipment.receiverName}
                  </p>
                </div>

                <button
                  onClick={toggleSimulation}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                    simulationActive
                      ? "bg-amber-600 hover:bg-amber-700 text-white shadow"
                      : "bg-emerald-600 hover:bg-emerald-700 text-white shadow"
                  }`}
                >
                  {simulationActive ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  <span>{simulationActive ? "Pause Sim" : "Auto Sim"}</span>
                </button>
              </div>

              {/* Status sequence triggers */}
              <div className="space-y-4">
                <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Database className="w-4 h-4 text-rose-500 animate-pulse" />
                  Override Package Status
                </h4>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    "booked",
                    "dispatched",
                    "in_transit",
                    "reached_hub",
                    "out_for_delivery",
                    "delivered",
                  ].map((st) => {
                    const isActive = selectedShipment.status === st;
                    return (
                      <button
                        key={st}
                        onClick={() => handleStatusChange(st)}
                        className={`p-3.5 rounded-xl border text-center transition-all text-xs font-bold cursor-pointer ${
                          isActive
                            ? "bg-rose-600 border-rose-600 text-white shadow-md shadow-rose-900/10"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300"
                        }`}
                      >
                        {st.replace("_", " ")}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Operations logs */}
              <div className="bg-slate-950 text-slate-300 rounded-xl p-4 font-mono text-[11px] space-y-2 border border-slate-850">
                <div className="flex gap-2 text-rose-400">
                  <span>[SYSTEM_LOG]</span>
                  <span>Awaiting incoming webhook trigger events...</span>
                </div>
                <div className="flex gap-2 text-slate-500">
                  <span>&gt;</span>
                  <span>Selected dispatch AWB: "{selectedShipment.awb}"</span>
                </div>
                <div className="flex gap-2 text-slate-500">
                  <span>&gt;</span>
                  <span>
                    Current location coordinates mapped: Lat{" "}
                    {selectedShipment.currentLocation.lat.toFixed(4)}, Lng{" "}
                    {selectedShipment.currentLocation.lng.toFixed(4)}
                  </span>
                </div>
                <div className="flex gap-2 text-emerald-400">
                  <span>&gt;</span>
                  <span>
                    Sorting classification: {selectedShipment.serviceType}{" "}
                    standard delivery SLA.
                  </span>
                </div>
              </div>

              {/* Guidelines */}
              <div className="text-xs text-slate-400 flex items-start gap-2 pt-4 border-t border-slate-100 leading-relaxed">
                <ShieldAlert className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <p>
                  Any updates made here update the shared application state in
                  real time. Switch tabs to <strong>Track Order</strong> or{" "}
                  <strong>Merchant Portal</strong> to inspect the updated
                  timeline checklist and interactive vehicle node path.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400">
            No shipments found. Book one first from the merchant portal.
          </div>
        )}
      </div>
    </div>
  );
}
